import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import {
  TbPlus,
  TbEdit,
  TbPig,
  TbUsers,
  TbCrown,
  TbChevronLeft,
  TbTrendingUp,
  TbCalendar,
  TbTarget,
  TbMoneybag,
} from 'react-icons/tb';

import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import ModalLayout from '../../components/layouts/ModalLayout';
import Input from '../../components/misc/Input';
import Labeled from '../../components/misc/Labeled';
import { formatEuroCents } from '../../../utils/bankAccountUtil';
import { formatDate } from '../../../utils/stringUtils';
import potService, {
  PotWithDetails,
  PotParticipant,
  PotTransaction,
  AddParticipantsDto,
  PotTransactionType,
  ParticipantBalance,
} from '../../../services/community/potService';
import useSelectedDashboardId from '../../../hooks/community/useSelectedDashboardId';
import { RootState } from '../../../redux/reducers';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import connectionService from '../../../services/community/connectionService';
import { SettingsPageTab } from './SettingsPage';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
} from '../../components/tables/BaseTable';
import PageWithTabsLayout from '../../components/layouts/PageWithTabsLayout';

type TabType = 'overview' | 'participants' | 'transactions';

export default function PotDetailsPage(): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.potDetails' });
  const { potId } = useParams<{ potId: string }>();
  const navigate = useNavigate();
  const dashboardId = useSelectedDashboardId();
  const connections = (useSelector((state: RootState) => state.connections) ?? []).filter(
    (c) => c.type === ConnectionType.MEMBER || c.type === ConnectionType.EXTERNAL_MEMBER,
  );

  const [pot, setPot] = useState<PotWithDetails | null>(null);
  const [transactions, setTransactions] = useState<PotTransaction[]>([]);
  const [participantBalances, setParticipantBalances] = useState<Record<number, ParticipantBalance>>({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Modal states
  const [isAddParticipantModalOpen, setIsAddParticipantModalOpen] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);

  useEffect(() => {
    if (connections.length === 0) {
      connectionService.getConnections();
    }
  }, [connections.length]);

  useEffect(() => {
    if (dashboardId && potId) loadPotDetails();
  }, [dashboardId, potId]);

  const loadPotDetails = async () => {
    if (!dashboardId || !potId) return;
    const numericPotId = parseInt(potId, 10);
    if (Number.isNaN(numericPotId)) {
      toast.error(t('errors.loadFailed'));
      navigate('/financial/pots');
      return;
    }
    setLoading(true);
    try {
      const potData = await potService.getPot(dashboardId, numericPotId);

      // Ensure participants are populated; fall back to dashboardMemberIds when necessary
      let participants: PotParticipant[] = Array.isArray(potData.participants)
        ? potData.participants
        : [];
      if (participants.length === 0 && Array.isArray(potData.dashboardMemberIds) && potData.dashboardMemberIds.length > 0) {
        participants = potData.dashboardMemberIds.map((memberId) => {
          const connection = connections.find((c) => c.dashboardMemberId === memberId);
          const name = connection
            ? `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`
            : `Member ${memberId}`;
          return {
            dashboardMemberId: memberId,
            name,
            isAdmin: false,
            isActive: true,
            sharePercentage: 0,
            currentBalance: 0,
          };
        });
      }

      const enrichedPot: PotWithDetails = {
        ...potData,
        participants,
      };
      setPot(enrichedPot);

      let transactionsList: PotTransaction[] = [];
      try {
        const transactionsData = await potService.getTransactions(dashboardId, numericPotId, {
          page: 1,
          limit: 50,
        });
        transactionsList = transactionsData?.transactions ?? [];
      } catch (txErr) {
        console.error('Failed to load pot transactions', txErr);
      }
      setTransactions(transactionsList);

      const balances: Record<number, ParticipantBalance> = {};
      await Promise.all(
        participants.map(async (p) => {
          try {
            const balance = await potService.getParticipantBalance(
              dashboardId,
              numericPotId,
              p.dashboardMemberId,
            );
            balances[p.dashboardMemberId] = balance;
          } catch (err) {
            console.error(`Failed to load balance for participant ${p.dashboardMemberId}`, err);
          }
        }),
      );
      setParticipantBalances(balances);
    } catch (err) {
      console.error(err);
      toast.error(t('errors.loadFailed'));
      navigate('/financial/pots');
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipants = async () => {
    if (!dashboardId || !potId || selectedMemberIds.length === 0) {
      toast.error(t('errors.selectParticipants'));
      return;
    }

    try {
      const dto: AddParticipantsDto = {
        // API expects the complete set of participant IDs for the pot
        dashboardMemberIds: selectedMemberIds,
      };

      await potService.addParticipants(dashboardId, parseInt(potId, 10), dto);
      await loadPotDetails();
      setIsAddParticipantModalOpen(false);
      setSelectedMemberIds([]);
      toast.success(t('success.participantsAdded'));
    } catch (err) {
      console.error(err);
      toast.error(t('errors.addParticipantsFailed'));
    }
  };

  const handleRemoveParticipant = async (participant: PotParticipant) => {
    if (!dashboardId || !potId) return;

    if (!window.confirm(t('confirmations.removeParticipant', { name: participant.name }))) return;

    try {
      await potService.removeParticipant(dashboardId, parseInt(potId, 10), participant.dashboardMemberId);
      await loadPotDetails();
      toast.success(t('success.participantRemoved'));
    } catch (err) {
      console.error(err);
      toast.error(t('errors.removeParticipantFailed'));
    }
  };

  const handleToggleParticipantAdmin = async (participant: PotParticipant) => {
    if (!dashboardId || !potId) return;

    try {
      await potService.updateParticipantRole(
        dashboardId,
        parseInt(potId, 10),
        participant.dashboardMemberId,
        { isAdmin: !participant.isAdmin },
      );
      await loadPotDetails();
      toast.success(t('success.participantUpdated'));
    } catch (err) {
      console.error(err);
      toast.error(t('errors.updateParticipantFailed'));
    }
  };

  // All members that can be linked to this pot; current participants will be preselected
  const availableMembers = connections;

  const calculateProgress = (current: number, target?: number): number => {
    if (!target || target === 0) return 0;
    return Math.min(100, Math.max(0, (current / target) * 100));
  };

  const getDaysUntilDeadline = (deadline?: Date | string): number | null => {
    if (!deadline) return null;
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getParticipantColor = (index: number): string => {
    const colors = ['#0A1B89', '#F19E7A', '#8AC285', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#6366F1'];
    return colors[index % colors.length];
  };

  const getTransactionTypeLabel = (type: PotTransactionType): string => {
    const labels: Record<PotTransactionType, string> = {
      [PotTransactionType.DEPOSIT]: t('transactionTypes.deposit'),
      [PotTransactionType.WITHDRAWAL]: t('transactionTypes.withdrawal'),
      [PotTransactionType.SPENDING]: t('transactionTypes.spending'),
      [PotTransactionType.ADJUSTMENT]: t('transactionTypes.adjustment'),
      [PotTransactionType.REFUND]: t('transactionTypes.refund'),
    };
    return labels[type] || type;
  };

  const getTransactionTypeColor = (type: PotTransactionType): string => {
    const colors: Record<PotTransactionType, string> = {
      [PotTransactionType.DEPOSIT]: 'text-success',
      [PotTransactionType.WITHDRAWAL]: 'text-danger',
      [PotTransactionType.SPENDING]: 'text-orange-600',
      [PotTransactionType.ADJUSTMENT]: 'text-blue-600',
      [PotTransactionType.REFUND]: 'text-green-600',
    };
    return colors[type] || 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-500">{t('loading')}</p>
      </div>
    );
  }

  if (!pot) {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-500">{t('errors.potNotFound')}</p>
      </div>
    );
  }

  const safeTotalBalance = Number.isFinite(pot.totalBalance as number) ? pot.totalBalance : 0;
  const safeTargetAmount = Number.isFinite(pot.targetAmount as number) ? pot.targetAmount : undefined;

  return (
    <>
      <Button
        variant="secondary"
        onClick={() =>
          navigate('/financial/settings', {
            state: { activeTab: SettingsPageTab.SAVING_POTS },
          })
        }
        className="mb-4">
        <TbChevronLeft />
        {t('backToPots')}
      </Button>

      {/* Hero Section */}
      <div className="mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-secondary-100 via-secondary-50 to-white p-8 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-6">
            {/* Large Pot Icon with Gradient */}
            <div
              className="flex h-20 w-20 items-center justify-center rounded-2xl shadow-lg md:h-24 md:w-24"
              style={{
                background: `linear-gradient(135deg, ${pot.color || '#0A1B89'} 0%, ${pot.color || '#0A1B89'}dd 100%)`,
                boxShadow: `0 8px 24px ${pot.color || '#0A1B89'}33`,
              }}>
              <TbPig className="h-12 w-12 text-white md:h-14 md:w-14" />
            </div>

            <div>
              <h1 className="mb-2 font-display text-3xl font-bold text-gray-900 md:text-4xl">{pot.name}</h1>
              {pot.description && (
                <p className="max-w-2xl text-lg text-gray-600">{pot.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats Row */}
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="flex items-center space-x-3 rounded-xl bg-white p-4 shadow-sm">
            <div className="rounded-lg bg-primary bg-opacity-10 p-2">
              <TbMoneybag className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-xs text-gray-500">{t('currentBalance')}</p>
              <p className={`text-xl font-bold ${safeTotalBalance < 0 ? 'text-danger' : 'text-primary-900'}`}>
                {formatEuroCents(safeTotalBalance, i18n.language)}
              </p>
            </div>
          </div>

          {safeTargetAmount != null && (
            <div className="flex items-center space-x-3 rounded-xl bg-white p-4 shadow-sm">
              <div className="rounded-lg bg-success bg-opacity-10 p-2">
                <TbTarget className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('targetAmount')}</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatEuroCents(safeTargetAmount, i18n.language)}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-3 rounded-xl bg-white p-4 shadow-sm">
            <div className="rounded-lg bg-primary-300 bg-opacity-30 p-2">
              <TbUsers className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-xs text-gray-500">{t('participants')}</p>
              <p className="text-xl font-bold text-gray-900">
                {(() => {
                  const activeParticipants = (pot.participants ?? []).filter((p) => p.isActive).length;
                  if (activeParticipants > 0) return activeParticipants;
                  const memberIds = pot.dashboardMemberIds;
                  return Array.isArray(memberIds) ? memberIds.length : 0;
                })()}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3 rounded-xl bg-white p-4 shadow-sm">
            <div className="rounded-lg bg-secondary bg-opacity-20 p-2">
              <TbTrendingUp className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-xs text-gray-500">{t('transactions')}</p>
              <p className="text-xl font-bold text-gray-900">{pot.transactionCount}</p>
            </div>
          </div>
        </div>

        {/* Progress Section */}
        {safeTargetAmount != null && (
          <div className="mt-6">
            <div className="mb-3 flex items-end justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('progress')}</p>
                <p className="text-3xl font-bold text-primary">
                  {Math.round(calculateProgress(safeTotalBalance, safeTargetAmount))}%
                </p>
              </div>
              {pot.targetDeadline && getDaysUntilDeadline(pot.targetDeadline) !== null && (
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <TbCalendar className="h-4 w-4" />
                  <span>
                    {getDaysUntilDeadline(pot.targetDeadline)! > 0
                      ? `${getDaysUntilDeadline(pot.targetDeadline)} days left`
                      : 'Deadline passed'}
                  </span>
                </div>
              )}
            </div>
            
            {/* Enhanced Progress Bar with Milestones */}
            <div className="relative h-4 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${calculateProgress(pot.totalBalance, pot.targetAmount)}%`,
                  background: `linear-gradient(90deg, ${pot.color || '#0A1B89'} 0%, ${pot.color || '#0A1B89'}dd 100%)`,
                }} />
              {/* Milestone Markers */}
              {[25, 50, 75].map((milestone) => (
                <div
                  key={milestone}
                  className="absolute top-0 h-full w-0.5 bg-white opacity-40"
                  style={{ left: `${milestone}%` }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <PageWithTabsLayout
        tabs={['overview', 'participants', 'transactions'] as TabType[]}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        renderTabContent={(tab: TabType) => {
          if (tab === 'overview') {
            return (
              <div className="space-y-6">
                {/* Recent Transactions */}
                <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">{t('recentTransactions')}</h3>
                    {transactions.length > 5 && (
                      <Button
                        variant="secondary"
                        onClick={() => setActiveTab('transactions')}>
                        {t('viewAllTransactions')}
                      </Button>
                    )}
                  </div>
                  
                  {transactions.length === 0 ? (
                    <div className="py-8 text-center">
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                        <TbMoneybag className="h-6 w-6 text-gray-400" />
                      </div>
                      <p className="mt-2 text-sm text-gray-500">{t('noTransactions')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transactions.slice(0, 5).map((transaction) => (
                        <div
                          key={transaction.transactionId}
                          className="flex items-center justify-between rounded-lg border border-gray-100 p-3 transition-colors hover:bg-gray-50">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className={`text-sm font-medium ${getTransactionTypeColor(transaction.type)}`}>
                                {getTransactionTypeLabel(transaction.type)}
                              </span>
                              {transaction.description && (
                                <span className="text-xs text-gray-500">• {transaction.description}</span>
                              )}
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              {transaction.performedByName} • {formatDate(transaction.createdAt)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p
                              className={`text-lg font-semibold ${transaction.amount > 0 ? 'text-success' : 'text-danger'}`}>
                              {transaction.amount > 0 ? '+' : ''}
                              {formatEuroCents(transaction.amount, i18n.language)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatEuroCents(transaction.balanceAfter, i18n.language)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          }

          if (tab === 'participants') {
            const activeParticipants = (pot.participants ?? []).filter((p) => p.isActive);
            const sortedParticipants = [...activeParticipants].sort((a, b) => {
              if (a.isAdmin !== b.isAdmin) return a.isAdmin ? -1 : 1;
              return b.currentBalance - a.currentBalance;
            });

            return (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('participantsList')}</h3>
                    <p className="text-sm text-gray-500">
                      {activeParticipants.length} {activeParticipants.length === 1 ? 'member' : 'members'}
                    </p>
                  </div>
                  <Button
                    variant="primary-dark"
                    onClick={() => {
                      // Preselect all existing participants in the modal so saving sends the full set
                      const currentParticipantIds = (pot.participants ?? []).map(
                        (p) => p.dashboardMemberId,
                      );
                      setSelectedMemberIds(currentParticipantIds);
                      setIsAddParticipantModalOpen(true);
                    }}>
                    <TbPlus className="h-4 w-4" />
                    {t('addParticipant')}
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {sortedParticipants.map((participant, index) => {
                    const balance = participantBalances[participant.dashboardMemberId];
                    const avatarColor = getParticipantColor(index);
                    
                    return (
                      <div
                        key={participant.dashboardMemberId}
                        className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br p-0.5 shadow-sm transition-all duration-300 hover:shadow-md ${
                          participant.isAdmin
                            ? 'from-yellow-200 via-yellow-100 to-white'
                            : 'from-secondary-200 via-secondary-100 to-white'
                        }`}>
                        <div className="relative h-full rounded-xl bg-white p-5">
                          {/* Admin Badge */}
                          {participant.isAdmin && (
                            <div className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-yellow-500 shadow-sm">
                              <TbCrown className="h-4 w-4 text-white" />
                            </div>
                          )}

                          {/* Avatar and Name */}
                          <div className="mb-4 flex items-center space-x-3">
                            <div
                              className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full text-lg font-bold text-white shadow-md"
                              style={{ backgroundColor: avatarColor }}>
                              {getInitials(participant.name)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="truncate font-semibold text-gray-900">{participant.name}</p>
                              <p className="text-sm text-gray-500">
                                {participant.sharePercentage.toFixed(1)}% share
                              </p>
                            </div>
                          </div>

                          {/* Balance Display */}
                          <div className="mb-3 rounded-lg bg-gradient-to-br from-secondary-50 to-transparent p-3">
                            <p className="text-xs text-gray-500">{t('balance')}</p>
                            <p
                              className={`text-2xl font-bold ${participant.currentBalance < 0 ? 'text-danger' : 'text-primary-900'}`}>
                              {formatEuroCents(participant.currentBalance, i18n.language)}
                            </p>
                          </div>

                          {/* Contribution Stats */}
                          {balance && (
                            <div className="space-y-2 border-t border-gray-100 pt-3">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">{t('totalDeposits')}</span>
                                <span className="font-medium text-success">
                                  +{formatEuroCents(balance.totalDeposits, i18n.language)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">{t('totalWithdrawals')}</span>
                                <span className="font-medium text-orange-600">
                                  -{formatEuroCents(Math.abs(balance.totalWithdrawals), i18n.language)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between border-t border-gray-100 pt-2 text-sm font-semibold">
                                <span className="text-gray-700">{t('netContribution')}</span>
                                <span className={balance.netContribution >= 0 ? 'text-success' : 'text-danger'}>
                                  {formatEuroCents(balance.netContribution, i18n.language)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {activeParticipants.length === 0 && (
                  <div className="rounded-2xl bg-gradient-to-br from-secondary-100 via-secondary-50 to-white p-12 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary bg-opacity-10">
                      <TbUsers className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="mb-2 font-semibold text-gray-900">{t('noParticipants.title')}</h3>
                    <p className="mb-4 text-sm text-gray-500">{t('noParticipants.description')}</p>
                    <Button variant="primary-dark" onClick={() => setIsAddParticipantModalOpen(true)}>
                      <TbPlus />
                      {t('addParticipant')}
                    </Button>
                  </div>
                )}
              </div>
            );
          }

          if (tab === 'transactions') {
            return (
              <div className="rounded-xl bg-white shadow-sm ring-1 ring-gray-100">
                <div className="border-b border-gray-100 p-4 md:p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{t('transactions')}</h3>
                  <p className="mt-1 text-sm text-gray-500">{t('table.description')}</p>
                </div>

                <BaseTable>
                  <BaseTableHeader>
                    <BaseTableHeaderRow>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 md:px-6">
                        {t('table.date')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 md:px-6">
                        {t('table.type')}
                      </th>
                      <th className="hidden px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 md:table-cell">
                        {t('table.description')}
                      </th>
                      <th className="hidden px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 lg:table-cell">
                        {t('table.performedBy')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 md:px-6">
                        {t('table.amount')}
                      </th>
                      <th className="hidden px-6 py-3 text-right text-xs font-medium uppercase text-gray-500 md:table-cell">
                        {t('table.balance')}
                      </th>
                    </BaseTableHeaderRow>
                  </BaseTableHeader>
                  <BaseTableBody>
                    {transactions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-16 text-center">
                          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                            <TbMoneybag className="h-6 w-6 text-gray-400" />
                          </div>
                          <p className="mt-2 text-sm text-gray-500">{t('noTransactions')}</p>
                        </td>
                      </tr>
                    ) : (
                      transactions.map((transaction) => (
                        <BaseTableDataRow key={transaction.transactionId} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-900 md:px-6">
                            <div className="font-medium">{formatDate(transaction.createdAt).split(',')[0]}</div>
                            <div className="text-xs text-gray-500 md:hidden">{transaction.performedByName}</div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-4 text-sm md:px-6">
                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getTransactionTypeColor(transaction.type)} bg-opacity-10`}
                              style={{ backgroundColor: `${getTransactionTypeColor(transaction.type).replace('text-', '')}22` }}>
                              {getTransactionTypeLabel(transaction.type)}
                            </span>
                          </td>
                          <td className="hidden px-6 py-4 text-sm text-gray-500 md:table-cell">
                            <div className="max-w-xs truncate">{transaction.description || '-'}</div>  
                          </td>
                          <td className="hidden whitespace-nowrap px-6 py-4 text-sm text-gray-900 lg:table-cell">
                            {transaction.performedByName}
                          </td>
                          <td
                            className={`whitespace-nowrap px-4 py-4 text-right text-sm font-semibold md:px-6 ${
                              transaction.amount > 0 ? 'text-success' : 'text-danger'
                            }`}>
                            <div>
                              {transaction.amount > 0 ? '+' : ''}
                              {formatEuroCents(transaction.amount, i18n.language)}
                            </div>
                            <div className="text-xs font-normal text-gray-500 md:hidden">
                              {formatEuroCents(transaction.balanceAfter, i18n.language)}
                            </div>
                          </td>
                          <td className="hidden whitespace-nowrap px-6 py-4 text-right text-sm text-gray-900 md:table-cell">
                            {formatEuroCents(transaction.balanceAfter, i18n.language)}
                          </td>
                        </BaseTableDataRow>
                      ))
                    )}
                  </BaseTableBody>
                </BaseTable>
              </div>
            );
          }

          return <></>;
        }}
      />

      {/* Add Participant Modal */}
      <ModalLayout
        open={isAddParticipantModalOpen}
        setOpen={setIsAddParticipantModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">{t('addParticipantModal.title')}</h2>
          <div className="space-y-4">
            <Labeled label={t('addParticipantModal.selectMembers')}>
              <div className="max-h-60 space-y-2 overflow-y-auto rounded-md border border-gray-300 p-3">
                {availableMembers.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('addParticipantModal.noAvailableMembers')}</p>
                ) : (
                  availableMembers.map((member) => (
                    <label key={member.dashboardMemberId} className="flex items-center space-x-2 hover:bg-gray-50 rounded px-2 py-1">
                      <input
                        type="checkbox"
                        checked={selectedMemberIds.includes(member.dashboardMemberId)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMemberIds((prev) => [...prev, member.dashboardMemberId]);
                          } else {
                            setSelectedMemberIds((prev) =>
                              prev.filter((id) => id !== member.dashboardMemberId),
                            );
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">
                        {member.fields.FIRST_NAME} {member.fields.LAST_NAME}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </Labeled>
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsAddParticipantModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button
                variant="primary-dark"
                onClick={handleAddParticipants}
                disabled={selectedMemberIds.length === 0}>
                <TbPlus className="h-4 w-4" />
                {t('addParticipant')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>
    </>
  );
}
